import { Pagination, Role, User, UserRoleNodeMapping } from '@ekkogmbh/apisdk';
import { Chip, Divider, Stack, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { UserAvatar } from 'src/Common/Components/UserAvatar';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { GenericDialog } from '../../Common/Components/GenericDialog';
import { DataTable } from '../../Common/Components/DataTable';
import { injectFakePagination } from '../../Common/Helper/Pagination';
import Permissions from 'src/Common/Permissions';
import { UserMappingPanel } from './UserMappingPanel';
import { DatePicker } from '@mui/x-date-pickers';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import { orange, red } from '@mui/material/colors';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { useContentControl } from 'src/Common/Stores/ContentControlProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { LoadingMask } from 'src/Common/Components/LoadingMask';
import { CenteredColumn } from 'src/Common/Components/Stacks';
import ObjectField from 'src/Common/Components/DataTable/ObjectField';

export const UserMappingsContent = (): React.JSX.Element => {
  const { api, hasPermission } = useEkko();
  const { setPanelOpen } = useContentControl();
  const { refresh } = usePagination();
  const { entityType, entityId } = useParams();
  const navigate = useNavigate();

  const [isFetching, startFetchingEntity] = useRequest<User | Role>();
  const [, startSaving] = useRequest();

  const [hasWritePermission, setWritePermission] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<'delete' | 'valid' | undefined>();
  const [user, setUser] = useState<User | undefined>();
  const [role, setRole] = useState<Role | undefined>();
  const [selectedMapping, setSelectedMapping] = useState<UserRoleNodeMapping>();

  /** on mount */
  useEffect(() => {
    setWritePermission(
      hasPermission(Permissions.MAPPINGS_WRITE) || hasPermission(Permissions.MAPPINGS_WRITE_RESTRICTED),
    );
  }, []);

  /** path param effect; fetch user/role entities */
  useEffect(() => {
    if (entityId === undefined || entityType === undefined) {
      setUser(undefined);
      setRole(undefined);
      return;
    }

    switch (entityType) {
      case 'user': {
        startFetchingEntity(
          () => api.getUser(Number(entityId)),
          (result) => {
            setUser(result as User);
            setRole(undefined);
          },
        );
        break;
      }
      case 'role': {
        startFetchingEntity(
          () => api.getRole(Number(entityId)),
          (result) => {
            setRole(result as Role);
            setUser(undefined);
          },
        );
        break;
      }
    }
  }, [entityType, entityId]);

  const fetchPaginatedItems = useCallback(
    async (pagination: Pagination) => {
      const mappings = await api.getUserRoleNodeMappings(user, role, undefined, true, true);
      return injectFakePagination<UserRoleNodeMapping>(mappings, pagination);
    },
    [user, role],
  );

  const updateHandler = useCallback(
    (userRoleNodeMapping: Required<Pick<UserRoleNodeMapping, 'id' | 'validUntil'>>) => {
      startSaving(
        () => api.updateUserRoleNodeMapping(userRoleNodeMapping),
        () => refresh(),
      );
    },
    [api],
  );

  const deleteHandler = useCallback(
    (userRoleNodeMapping: Required<Pick<UserRoleNodeMapping, 'id'>>) => {
      startSaving(
        () => api.deleteUserRoleNodeMapping(userRoleNodeMapping),
        () => refresh(),
      );
    },
    [api],
  );

  const onDismiss = () => {
    setOpenDialog(undefined);
    setPanelOpen(false);
    setSelectedMapping(undefined);
  };

  const onUpdateOk = useCallback(async () => {
    if (selectedMapping) {
      updateHandler(selectedMapping);
    }
  }, [selectedMapping, updateHandler]);

  const onDeleteOk = useCallback(async () => {
    if (!selectedMapping || !selectedMapping.id || selectedMapping.user === undefined || !selectedMapping.user.id) {
      return;
    }

    deleteHandler({
      id: selectedMapping.id,
    });
    onDismiss();
  }, [selectedMapping, deleteHandler]);

  const onEditValidUntil = (urnm: UserRoleNodeMapping) => {
    setSelectedMapping(urnm);
    setOpenDialog('valid');
  };

  const dialog = useMemo(() => {
    if (selectedMapping === undefined) {
      return <></>;
    }

    switch (openDialog) {
      case 'valid': {
        const validUntil = moment(selectedMapping.validUntil);
        return (
          <GenericDialog
            open
            type="confirmation"
            title={'Update Valid-Until'}
            text={
              <Stack spacing={2} sx={{ minWidth: 300, pt: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <UserAvatar username={selectedMapping.user!.username} size="large" />
                  <Stack spacing={0.5}>
                    <Typography fontWeight={600}>{selectedMapping.user!.username}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedMapping.role!.name} · {selectedMapping.node!.path}
                    </Typography>
                  </Stack>
                </Stack>
                <Divider />
                <DatePicker
                  label="Valid-Until"
                  disablePast
                  value={validUntil}
                  onChange={(date: Moment | null) => {
                    if (date !== null) {
                      selectedMapping.validUntil = date.format('YYYY-MM-DD');
                      setSelectedMapping(selectedMapping);
                    }
                  }}
                  minDate={moment().add(1, 'd')}
                  sx={{ width: '100%' }}
                />
              </Stack>
            }
            onClose={onDismiss}
            onConfirm={onUpdateOk}
          />
        );
      }
      case 'delete':
        return (
          <GenericDialog
            open
            fullWidth
            centered
            type="confirmation"
            maxWidth={'sm'}
            title={'Delete Mapping'}
            text={
              <CenteredColumn>
                <ObjectField
                  direction={'column'}
                  fields={[
                    { key: 'User', value: selectedMapping.user?.username ?? '' },
                    { key: 'Role', value: selectedMapping.role.name },
                    { key: 'Node', value: selectedMapping.node.coordinate },
                  ]}
                />
                <Typography style={{ margin: 16 }} variant={'caption'}>
                  This action can not be undone.
                </Typography>
              </CenteredColumn>
            }
            onClose={onDismiss}
            onConfirm={onDeleteOk}
          />
        );
      default:
        return <></>;
    }
  }, [openDialog, selectedMapping, onUpdateOk]);

  // Prevent rendering table before selected entity is present
  if ((entityType !== undefined && user === undefined && role === undefined) || isFetching) {
    return <LoadingMask />;
  }

  return (
    <ContentControl
      panel={<UserMappingPanel user={user} role={role} mapping={selectedMapping} closeHandler={onDismiss} />}
      dialog={dialog}
      buttons={
        hasWritePermission
          ? [
              <ContentControlButton
                key={'add'}
                content={<Add />}
                tooltip={'Add User-Role Mapping'}
                onClick={() => {
                  onDismiss();
                  setPanelOpen(true);
                }}
              />,
            ]
          : []
      }
    >
      <DataTable
        title={
          entityType === 'user'
            ? 'Roles of user: ' + (user?.username ?? '?')
            : 'Users with role: ' + (role?.name ?? '?')
        }
        onBack={() => navigate(-1)}
        fetchItems={fetchPaginatedItems}
        actions={[
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: UserRoleNodeMapping) => {
              setSelectedMapping(entry);
              setOpenDialog('delete');
            },
            disabled: !hasWritePermission,
          },
        ]}
        // @FIXME: missing proper filters
        columns={[
          {
            header: 'User',
            body: (entry: UserRoleNodeMapping) => entry.user!.username,
          },
          {
            header: 'Role',
            body: (entry: UserRoleNodeMapping) => entry.role.name,
          },
          {
            header: 'Coordinate',
            body: (entry: UserRoleNodeMapping) => entry.node.coordinate,
          },
          {
            header: 'Valid-Until',
            body: (entry: UserRoleNodeMapping) => {
              if (entry.user?.automation) {
                return (
                  <Chip
                    label="No expiry"
                    size="small"
                    sx={{
                      backgroundColor: 'action.selected',
                      color: 'text.secondary',
                      fontWeight: 500,
                      fontSize: 11,
                      height: 20,
                    }}
                  />
                );
              }
              const momentDate = moment(entry.validUntil);
              const hoursDiff = momentDate.diff(moment(), 'hours');
              const isExpired = hoursDiff <= 0;
              const isExpiringSoon = hoursDiff > 0 && hoursDiff < 48;
              const relativeTime = momentDate.fromNow();

              return (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    onClick={() => onEditValidUntil(entry)}
                    sx={{
                      cursor: 'pointer',
                      borderBottom: '1px dashed',
                      borderColor: 'text.secondary',
                      '&:hover': {
                        borderColor: 'primary.main',
                        color: 'primary.main',
                      },
                    }}
                  >
                    {momentDate.format('DD MMM YYYY')}
                  </Typography>
                  <Chip
                    label={relativeTime}
                    size="small"
                    sx={{
                      backgroundColor: isExpired ? red[500] : isExpiringSoon ? orange[500] : 'action.selected',
                      color: isExpired || isExpiringSoon ? 'white' : 'text.secondary',
                      fontWeight: 500,
                      fontSize: 11,
                      height: 20,
                    }}
                  />
                </Stack>
              );
            },
          },
        ]}
      />
    </ContentControl>
  );
};
